import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model/selectors';
import { useFundingSources } from './use-funding-sources';

/**
 * Hook to access coverage assessment data for the guided planning feature.
 *
 * For completed sessions, uses the snapshotted multipleFundingSources.
 * For active sessions, uses the live client data.
 *
 * This is the guided-planning-specific version of useCoverageAssessmentData.
 */
export function useCoverageAssessment() {
  const client = useSelector(selectClient);
  const fundingSources = useFundingSources();

  // Round yearsTillLtc for display purposes, minimum 0
  const yearsTillLtc = useMemo(
    () => Math.max(0, Math.round(client.appliedInferenceSet.yearsTillLtc)),
    [client.appliedInferenceSet.yearsTillLtc],
  );

  // Direct access to pre-calculated costs
  const allPhaseInflatedProfessionalShareCost =
    client.allPhaseCosts.allPhaseInflatedProfessionalShareCost;

  // Use snapshotted funding sources for completed sessions
  const multipleSelfFundingProjectedCostCoverage =
    fundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;

  // Calculate the gap between cost and coverage
  const coverageGap = useMemo(
    () =>
      Math.max(0, allPhaseInflatedProfessionalShareCost - multipleSelfFundingProjectedCostCoverage),
    [allPhaseInflatedProfessionalShareCost, multipleSelfFundingProjectedCostCoverage],
  );

  // Count of self-funding sources
  const selfFundingSourceCount = useMemo(
    () => Object.keys(fundingSources?.selfFunding ?? {}).length,
    [fundingSources?.selfFunding],
  );

  return {
    yearsTillLtc,
    allPhaseInflatedProfessionalShareCost,
    multipleSelfFundingProjectedCostCoverage,
    coverageGap,
    selfFundingSourceCount,
  };
}
